/**
 * Shared helpers for unified-runtime agent sessions.
 *
 * Skillflows-prune Phase 1.C — extracted from forge so the next units
 * (Saga, Maestro, Geo, …) get the race-free `ensure` semantics for
 * free.
 *
 * The single in-flight cache below collapses concurrent
 * `/core/agent/start` calls keyed by (agentName, workspaceId) into one
 * shared promise. Without it, React StrictMode (or any quick
 * double-render that triggers an effect twice) fires two parallel
 * requests, both miss the Django `runtime_session_ensure` SELECT, and
 * both INSERTs win — leaving twin AgentRuntimeSession rows for the
 * same workspace. The newer row then "wins"
 * `order_by(-created_at).first()` on the next refresh while the chat
 * events live on the original row, so the wizard comes up empty.
 *
 * The matching backend fix (`unique_workspace_agent_session` partial
 * constraint + `transaction.atomic` IntegrityError fallback in
 * `runtime_session_ensure`) is the durable defense; this client cache
 * just keeps a clean dev experience and shaves one round-trip.
 */
import { apiFetch } from "@/lib/jwt-auth-client";
import { z } from "zod";

const inFlight = new Map<string, Promise<string | null>>();

interface EnsureRuntimeSessionOptions {
  /** Resume a specific existing session by id (Aigentise Phase 1).
   * Singleton orchestrators flip their `WorkspaceActiveSession`
   * pointer to this id on the backend, so subsequent page loads
   * resume from this session. */
  sessionId?: string;
  /** When true, bypass the in-flight de-dup cache so this call always
   * hits the backend. Used by the "Start new" action to force a fresh
   * session row even when a prior ensure for the same (agent,
   * workspace) is still pending. */
  forceNew?: boolean;
  /** Optional title persisted on newly-created sessions. */
  title?: string;
}

const RuntimeWorkspaceSessionSchema = z.object({
  id: z.string(),
  agentName: z.string(),
  status: z.string(),
  isActive: z.boolean(),
  createdAt: z.string().nullable(),
  lastActivityAt: z.string().nullable(),
  parentSessionId: z.string().nullable(),
  title: z.string(),
  messageCount: z.number().optional(),
});

const RuntimeWorkspaceSessionsResponseSchema = z.object({
  sessions: z.array(RuntimeWorkspaceSessionSchema),
  activeSessionId: z.string().nullable(),
  activeSessionStatus: z.string().nullable(),
});

export type RuntimeWorkspaceSessionSummary = z.infer<typeof RuntimeWorkspaceSessionSchema>;

export interface RuntimeWorkspaceSessionsResult {
  sessions: RuntimeWorkspaceSessionSummary[];
  activeSessionId: string | null;
  activeSessionStatus: string | null;
}

/**
 * Returns an AgentRuntimeSession UUID for (current user, workspaceId,
 * agentName).
 *
 * - Without `sessionId`: backend creates a new session for singleton
 *   orchestrators (Aigentise Phase 1 / Step 1.2) and flips the active
 *   pointer to it; non-singleton agents also get a fresh row.
 * - With `sessionId`: backend returns that specific session and (for
 *   singletons) flips the active pointer to it.
 *
 * The module-level in-flight cache collapses concurrent React
 * StrictMode double-renders into one request. `forceNew` bypasses it
 * for the "Start new" UX where we always want a fresh row.
 *
 * Returns `null` if the request fails.
 */
export async function ensureRuntimeSession(
  agentName: string,
  workspaceId: string,
  options: EnsureRuntimeSessionOptions = {},
): Promise<string | null> {
  // Cache key includes sessionId so resume calls don't collide with
  // mount-time "ensure" calls for the same (agent, workspace).
  const key = `${agentName}|${workspaceId}|${options.sessionId ?? ""}`;
  if (!options.forceNew) {
    const existing = inFlight.get(key);
    if (existing) return existing;
  }

  const promise = (async () => {
    try {
      const body: Record<string, unknown> = { agentName, workspaceId };
      if (options.sessionId) body.sessionId = options.sessionId;
      if (options.title) body.title = options.title;
      const res = await apiFetch("/core/agent/start", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) return null;
      const data = (await res.json()) as { sessionId?: string };
      return typeof data.sessionId === "string" ? data.sessionId : null;
    } catch {
      return null;
    } finally {
      setTimeout(() => {
        inFlight.delete(key);
      }, 1_000);
    }
  })();
  if (!options.forceNew) inFlight.set(key, promise);
  return promise;
}

export async function listRuntimeWorkspaceSessions(
  agentName: string,
  workspaceId: string,
  limit = 50,
): Promise<RuntimeWorkspaceSessionsResult | null> {
  try {
    const params = new URLSearchParams({
      agentName,
      limit: String(limit),
    });
    const res = await apiFetch(
      `/core/agent/workspace/${encodeURIComponent(workspaceId)}/sessions?${params.toString()}`,
    );
    if (!res.ok) return null;
    return RuntimeWorkspaceSessionsResponseSchema.parse(await res.json());
  } catch {
    return null;
  }
}

/**
 * Persist a relevant title on a runtime session.
 *
 * Two modes (supply exactly one):
 *  - `title`        → store this exact title (slash-command preset / manual rename)
 *  - `firstMessage` → Core summarizes the first user turn into a short title
 *
 * Core derives + persists the title (Django `title` column) and returns the
 * final value so the caller can update its session list. Returns `null` if the
 * request fails — callers keep their current name in that case.
 */
export async function setRuntimeSessionTitle(params: {
  sessionId: string;
  title?: string;
  firstMessage?: string;
}): Promise<string | null> {
  try {
    const body: Record<string, unknown> = { sessionId: params.sessionId };
    if (params.title) body.title = params.title;
    else if (params.firstMessage) body.firstMessage = params.firstMessage;
    else return null;

    const res = await apiFetch("/core/agent/session-title", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { title?: string };
    return typeof data.title === "string" && data.title.length > 0 ? data.title : null;
  } catch {
    return null;
  }
}

/**
 * Resolve the active workspace session for a singleton orchestrator. Page
 * mounts should use this; "New chat" should call ensureRuntimeSession with
 * forceNew so it intentionally creates another row.
 */
export async function ensureActiveRuntimeSession(
  agentName: string,
  workspaceId: string,
): Promise<string | null> {
  const listed = await listRuntimeWorkspaceSessions(agentName, workspaceId);
  if (!listed) return null;
  const activeId =
    listed.sessions.find((session) => session.id === listed.activeSessionId)?.id ??
    listed.sessions.find((session) => session.isActive)?.id ??
    listed.sessions[0]?.id ??
    null;

  if (activeId) {
    const resumed = await ensureRuntimeSession(agentName, workspaceId, {
      sessionId: activeId,
    });
    return resumed ?? activeId;
  }

  return ensureRuntimeSession(agentName, workspaceId, { forceNew: true });
}
